import React from 'react';
import PropTypes from 'prop-types';
import {
  claimantLink,
  filesOrFormField,
  maskedIdentifier,
  shortDateOrBlank,
  statusField,
} from './CombinedHistoryFields';
import { ViewFilesButton, hasFiles } from './SubmissionFilesModal';
import {
  SUBMISSION_TYPES,
  submissionPropType,
} from '../utilities/combinedHistory';

/** @typedef {import('../utilities/combinedHistory').CombinedHistorySubmission} Submission */

/**
 * @param {Submission} submission
 * @returns {string} "QuickSubmit" for an upload, or "VA Form" and the form
 *   number, with "packet" for a packet.
 */
const cardTitle = submission => {
  if (submission.type === SUBMISSION_TYPES.QUICK_SUBMIT) {
    return SUBMISSION_TYPES.QUICK_SUBMIT;
  }
  return ['VA Form', submission.formType, submission.packet ? 'packet' : '']
    .filter(Boolean)
    .join(' ');
};

/**
 * One history row as a card: the title and submitted date, the claimant, the
 * received date, and the confirmation number, with the status badge and an
 * upload's file count and View button right-aligned beside them from tablet
 * width up, and left-aligned under them below it.
 *
 * @param {Object} props
 * @param {Submission} props.submission
 * @param {(submission: Submission) => void} props.onViewFiles Opens an
 *   upload's files.
 * @returns {JSX.Element} A list item for the card list.
 */
const CombinedHistoryCard = ({ submission, onViewFiles }) => {
  const status = statusField(submission);
  const claimant = claimantLink(submission);
  const identifier = maskedIdentifier(submission);
  const submittedOn = shortDateOrBlank(submission.submittedDate);
  const files =
    submission.type === SUBMISSION_TYPES.QUICK_SUBMIT
      ? filesOrFormField(submission).text
      : '';

  return (
    <li className="vads-u-margin-bottom--2">
      <va-card data-testid="combined-history-card">
        <div className="submissions__history-card tablet:vads-u-display--flex vads-u-flex-wrap--wrap vads-u-align-items--center vads-u-justify-content--space-between">
          <div className="submissions__history-card-details">
            <h3 className="vads-u-display--flex vads-u-flex-wrap--wrap vads-u-align-items--baseline vads-u-margin-y--0 vads-u-font-family--sans vads-u-font-size--source-sans-normalized">
              <span className="vads-u-margin-right--1p5">
                {cardTitle(submission)}
              </span>
              {submittedOn && (
                <>
                  {' '}
                  <span className="submissions__history-card-date">
                    Submitted {submittedOn}
                  </span>
                </>
              )}
            </h3>
            <p className="vads-u-margin--0">
              {claimant}
              {claimant && identifier && ' '}
              {identifier && (
                <span data-testid="masked-identifier">{identifier}</span>
              )}
            </p>
            {status.receivedOn && (
              <p className="vads-u-margin--0">{status.receivedOn}</p>
            )}
            {submission.confirmationNumber && (
              <p className="vads-u-margin--0">
                Confirmation #
                <span className="submissions__confirmation">
                  {submission.confirmationNumber}
                </span>
              </p>
            )}
          </div>
          <div className="submissions__history-card-status tablet:vads-u-text-align--right">
            {status.badge}
            {files && (
              <p className="submissions__status-date vads-u-margin-top--1 vads-u-margin-bottom--0 vads-u-font-size--sm">
                {files}
              </p>
            )}
            {hasFiles(submission) && (
              <p className="vads-u-margin-top--1 vads-u-margin-bottom--0">
                <ViewFilesButton submission={submission} onView={onViewFiles} />
              </p>
            )}
          </div>
        </div>
      </va-card>
    </li>
  );
};

CombinedHistoryCard.propTypes = {
  submission: submissionPropType.isRequired,
  onViewFiles: PropTypes.func.isRequired,
};

export default CombinedHistoryCard;
